// @ts-strict-ignore
import { useTranslation } from "react-i18next";

import { Arrange, Button, Skeleton, Spacer, Typography } from "@aviary";
import { BrandLink } from "@emerson/components/BrandLink/BrandLink";
import { emersonRoutes } from "@emerson/data/emersonRoutes";
import {
  useWordpressFeaturedItemsQuery,
  FeaturedBrandLocation,
} from "@emerson/data/queries/WordpressFeaturedItemsQuery/WordpressFeaturedItemsQuery.query";
import { l } from "@emerson/locales/i18n";
import { useBrandsQuery } from "@shared/data/queries/BrandsQuery/Brands.query";
import type {
  BrandSplinter,
  Variables as BrandsVariablesType,
} from "@shared/data/queries/BrandsQuery/Brands.query";
import { EmersonSearchApplyFilterEntryPoint } from "@shared/hooks/useAnalyticsLogger/EmersonAnalyticsEntryPoint";
import { isE2E } from "@shared/utils/fullscriptEnv/fullscriptEnv";

import { SidebarMenu } from "../SidebarMenu/SidebarMenu";

import * as styles from "./Brands.styles";

interface Props {
  onClose: () => void;
}

const Brands = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const {
    loading: loadingCoopFeaturedBrands,
    error: coopFeaturedBrandsError,
    data: coopFeaturedBrands,
  } = useWordpressFeaturedItemsQuery({
    variables: { type: "brand" },
    skip: isE2E(),
  });

  const currentBrands = [
    "Allergy Research Group",
    "Designs for Health",
    "Microbiome Labs",
    "Thorne",
  ];

  const { loading: loadingFeaturedBrands, data: featuredBrands } = useBrandsQuery({
    variables: {
      first: 16,
      filters: { active: true, list: "EE_TOP_BRANDS" },
    } as BrandsVariablesType,
  });

  const { loading: loadingPopularBrands, data: popularBrands } = useBrandsQuery({
    variables: {
      first: 10,
      filters: { active: true },
    } as BrandsVariablesType,
  });

  const renderFeaturedBrands = () => {
    if (loadingFeaturedBrands || loadingCoopFeaturedBrands) {
      return (
        <Arrange isFullWidth>
          <Arrange>
            <Skeleton type="image" imageSize="large" />
            <Skeleton type="image" imageSize="large" />
          </Arrange>
          <Arrange>
            <Skeleton type="image" imageSize="large" />
            <Skeleton type="image" imageSize="large" />
          </Arrange>
        </Arrange>
      );
    } else {
      let brands = featuredBrands.viewer.brands.nodes?.filter(brand =>
        currentBrands.includes(brand.name)
      );

      if (!coopFeaturedBrandsError) {
        const coopBrands = coopFeaturedBrands?.featuredItems?.nodes
          ?.filter(node => node.featuredBrandLocation === FeaturedBrandLocation.megamenu)
          .slice(0, 4)
          .map(item => {
            return {
              id: item.featuredBrandId,
              logo: item.featuredBrandImage,
              name: item.featuredBrandName,
            } as BrandSplinter;
          });
        if (coopBrands && coopBrands.length > 0) brands = coopBrands;
      }

      if (!brands.length) return null;

      return (
        <div css={styles.FeaturedBrandsGrid}>
          {brands.map(brand => (
            <BrandLink
              key={brand.id}
              brand={brand}
              onClick={onClose}
              entryPoint={EmersonSearchApplyFilterEntryPoint.BRANDS_MENU}
            >
              <img src={brand.logo} alt={brand.name} css={styles.FeaturedBrandsImage} />
            </BrandLink>
          ))}
        </div>
      );
    }
  };

  const renderPopularBrands = () => {
    if (loadingPopularBrands) {
      return [...Array(10)].map((elem, index) => {
        return <Skeleton key={index} type="typography" isFullWidth />;
      });
    } else {
      const brands = popularBrands.viewer.brands.nodes;

      return (
        <Arrange isVertical>
          {brands.map(brand => (
            <BrandLink
              brand={brand}
              isColor="system"
              isUnderLined={false}
              key={brand.id}
              onClick={onClose}
              entryPoint={EmersonSearchApplyFilterEntryPoint.BRANDS_MENU}
            >
              {brand.name}
            </BrandLink>
          ))}
          <Spacer height="half" />
        </Arrange>
      );
    }
  };

  return (
    <SidebarMenu title={t(l.header.Brands.Header)}>
      <Typography type="h5">{t(l.header.Brands.FeaturedBrands)}</Typography>
      {renderFeaturedBrands()}
      <Typography type="h5">{t(l.header.Brands.PopularBrands)}</Typography>
      {renderPopularBrands()}
      <Button to={emersonRoutes.brands} intention="system">
        {t(l.header.Brands.SeeAll)}
      </Button>
    </SidebarMenu>
  );
};

export { Brands };
